import { FormProvider, useForm } from 'react-hook-form';
import styles from './styles.module.scss';
import { UserPaymentDetailsFormContentProps } from './props';
import { Button, ErrorCard, ModalHeader } from '@starsoft/common/components';
import { motion } from 'framer-motion';
import { slideInRight } from './variants';
import { PropsWithChildren, useCallback, useMemo } from 'react';
import UserPaymentDetailsFormLayoutSkeleton from './Skeleton';
import { Nullable } from '@starsoft/common/interfaces';
import { AxiosError } from 'axios';
import { GenericError, UserPaymentDetails } from '@starsoft/common/models';
import {
  CreateUserPaymentDetailsInputDto,
  UpdateUserPaymentDetailsInputDto,
} from '@starsoft/common/dto';
import { KYCStatus, UserPaymentDetailsTypeId } from '@starsoft/common/enums';
import {
  useCreateUserPaymentDetails,
  useUpdateUserPaymentDetails,
  useUserPaymentDetailsById,
} from '../../../../../../../api';
import {
  useKycMeByOriginIdWithOrigin,
  useTranslation,
  useUserPaymentTypeIdByCoinId,
  useWithdrawSearchParams,
} from '@starsoft/common/hooks';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

/**
 * UserPaymentDetailsFormLayout Component
 * This component is responsible for rendering the user payment details form layout.
 * It handles form submission, validation, and displays the form content.
 *
 * @param {PropsWithChildren<UserPaymentDetailsFormContentProps>} props - The properties for the component.
 * @param {Function} props.onClose - The function to call when the form is closed.
 * @param {React.ReactNode} props.children - The child components to render within the form layout.
 *
 * @returns {JSX.Element} The rendered component displaying the user payment details form layout.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormLayout onClose={() => function('Form closed')}>
 *   <UserPaymentDetailsFormContent />
 * </UserPaymentDetailsFormLayout>
 */
export default function UserPaymentDetailsFormLayout({
  onClose,
  children,
}: PropsWithChildren<UserPaymentDetailsFormContentProps>) {
  const { t } = useTranslation('common');
  const methods = useForm<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto
  >();
  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    watch,
  } = methods;
  const { userPaymentDetails } = useWithdrawSearchParams();
  const userPaymentTypeIdByCoinId: Nullable<string> =
    useUserPaymentTypeIdByCoinId();
  const isCreating: boolean = userPaymentDetails == 'new';
  const pixKey: string | undefined = watch('pix.key');
  const accountHolder: string | undefined = watch('imps.accountHolder');
  const ifsc: string | undefined = watch('imps.ifsc');
  const accountNumber: string | undefined = watch('imps.accountNumber');
  const bankAccount: string | undefined = watch('bankAccount.account');

  const hasError: boolean = useMemo(
    () => Object.values(errors).some(error => !!error),
    [errors],
  );

  /**
   * getIsSubmitButtonDisabled Function
   * Determines if the submit button should be disabled based on the user payment type and form values.
   *
   * @returns {boolean} True if the submit button should be disabled, false otherwise.
   *
   * @example
   * // Usage example:
   * const isDisabled = getIsSubmitButtonDisabled();
   */
  function getIsSubmitButtonDisabled() {
    switch (userPaymentTypeIdByCoinId?.toString()) {
      case UserPaymentDetailsTypeId.Pix?.toString():
        return !pixKey;
      case UserPaymentDetailsTypeId.Imps?.toString():
        return !accountHolder || !accountNumber || !ifsc;
      case UserPaymentDetailsTypeId.MexicanBankAccount?.toString():
      case UserPaymentDetailsTypeId.ArgentineBankAccount?.toString():
      case UserPaymentDetailsTypeId.NigerianBankAccount?.toString():
      case UserPaymentDetailsTypeId.SingaporeBankAccount?.toString():
      case UserPaymentDetailsTypeId.SouthAfricaBankAccount?.toString():
        return !bankAccount;
    }
  }

  const isSubmitButtonDisabled =
    getIsSubmitButtonDisabled() || !isDirty || hasError;

  /**
   * getUserPaymentDetailsResetDto Function
   * Generates a reset DTO for user payment details based on the provided user payment details.
   *
   * @param {UserPaymentDetails} userPaymentDetails - The user payment details object.
   *
   * @returns {Pick<CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto, 'pix' | 'imps' | 'bankAccount'>} The reset DTO for user payment details.
   *
   * @example
   * // Usage example:
   * const resetDto = getUserPaymentDetailsResetDto(userPaymentDetails);
   */
  function getUserPaymentDetailsResetDto(
    userPaymentDetails: UserPaymentDetails,
  ): Pick<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto,
    'pix' | 'imps' | 'bankAccount'
  > {
    switch (userPaymentDetails.type.id?.toString()) {
      case UserPaymentDetailsTypeId.Pix?.toString():
        return {
          pix: {
            key: userPaymentDetails.pix.pixKey,
          },
        };
      case UserPaymentDetailsTypeId.Imps?.toString():
        return {
          imps: {
            accountHolder: userPaymentDetails.imps.accountHolder,
            ifsc: userPaymentDetails.imps.ifsc,
            accountNumber: userPaymentDetails.imps.accountNumber,
          },
        };
      case UserPaymentDetailsTypeId.ArgentineBankAccount?.toString():
      case UserPaymentDetailsTypeId.MexicanBankAccount?.toString():
        return {
          bankAccount: {
            account: userPaymentDetails?.bankAccount?.account,
            typeId: userPaymentDetails?.type?.id?.toString(),
            bankCode: userPaymentDetails?.bankAccount?.bank?.code,
          },
        };
    }
  }

  /**
   * handleExternalSuccess Function
   * Handles the external success event by resetting the form with the provided user payment details.
   *
   * @param {UserPaymentDetails} userPaymentDetails - The user payment details object.
   *
   * @example
   * // Usage example:
   * handleExternalSuccess(userPaymentDetails);
   */
  const handleExternalSuccess = useCallback(
    (userPaymentDetails: UserPaymentDetails) => {
      if (!userPaymentDetails) {
        return;
      }

      const userPaymentDetailsReset: Pick<
        CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto,
        'pix' | 'imps' | 'bankAccount'
      > = getUserPaymentDetailsResetDto(userPaymentDetails);

      reset({
        ...userPaymentDetailsReset,
        id: userPaymentDetails.id,
        typeId: userPaymentDetails.type.id?.toString(),
      });
    },
    [reset],
  );

  const {
    isLoading: isLoadingUserPaymentDetails,
    refetch: refetchUserPaymentDetails,
    error: errorUserPaymentDetails,
  } = useUserPaymentDetailsById({
    id: isCreating
      ? undefined
      : (parseToBigInt(userPaymentDetails ?? '0') as bigint),
    externalSuccess: handleExternalSuccess,
  });
  const {
    isPending: isPendingKyc,
    error: kycError,
    refetch: refetchKyc,
    kyc,
  } = useKycMeByOriginIdWithOrigin();
  const isPending: boolean = isLoadingUserPaymentDetails || isPendingKyc;
  const error: AxiosError<GenericError> = kycError ?? errorUserPaymentDetails;
  const {
    createUserPaymentDetails,
    isPending: isPendingCreateUserPaymentDetails,
  } = useCreateUserPaymentDetails();
  const {
    updateUserPaymentDetails,
    isPending: isPendingUpdateUserPaymentDetails,
  } = useUpdateUserPaymentDetails();
  const isFormPending: boolean =
    isPendingCreateUserPaymentDetails || isPendingUpdateUserPaymentDetails;

  /**
   * refetch Function
   * Refetches the KYC and user payment details data.
   *
   * @example
   * // Usage example:
   * refetch();
   */
  function refetch(): void {
    if (kycError) {
      refetchKyc();
    }

    if (errorUserPaymentDetails) {
      refetchUserPaymentDetails();
    }
  }

  /**
   * onSubmit Function
   * Handles the form submission event by creating or updating user payment details.
   *
   * @param {CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto} data - The form data.
   *
   * @example
   * // Usage example:
   * onSubmit({
   *   id: '1',
   *   identityId: '123',
   *   typeId: '456',
   *   pix: { key: 'pixKey' },
   *   imps: { accountHolder: 'John Doe', ifsc: 'IFSC123', accountNumber: '123456' },
   *   bankAccount: { account: '789', typeId: '456', bankCode: 'BANK123' },
   * });
   */
  function onSubmit({
    id,
    identityId,
    typeId,
    pix,
    imps,
    bankAccount,
  }: CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto) {
    const coallescedTypeId: Nullable<string> =
      typeId ?? userPaymentTypeIdByCoinId;

    if (isCreating) {
      createUserPaymentDetails({
        identityId,
        typeId: coallescedTypeId,
        pix,
        imps,
        bankAccount: {
          ...bankAccount,
          typeId: coallescedTypeId,
        },
      });
      return;
    }

    updateUserPaymentDetails({
      id,
      typeId: coallescedTypeId,
      pix,
      imps,
      bankAccount: {
        ...bankAccount,
        typeId: coallescedTypeId,
      },
    });
  }

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  if (!isPending && (!kyc || (!!kyc && kyc?.status !== KYCStatus.Verified))) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <motion.form
        id="user-details-account"
        onSubmit={handleSubmit(onSubmit)}
        className={styles.container}
        variants={slideInRight}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <ModalHeader
          title={t(
            isCreating ? 'create_payment_account' : 'update_payment_account',
          )}
          onClose={onClose}
          withBackButton
          handleGoBack={onClose}
          smallPadding
          hideCloseButton
        />

        {isPending ? (
          <UserPaymentDetailsFormLayoutSkeleton />
        ) : (
          <>
            <div className={styles.container__content}>{children}</div>
            <div className={styles.container__padded}>
              <Button
                className={styles.container__submit}
                type="submit"
                disabled={isSubmitButtonDisabled}
                isSecondary={isSubmitButtonDisabled}
                form="user-details-account"
                isloading={isFormPending}
              >
                {t(isCreating ? 'create_label' : 'update_label')}
              </Button>
            </div>
          </>
        )}
      </motion.form>
    </FormProvider>
  );
}
